import React from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { Plus, ChevronRight, ChevronLeft, Package, Filter } from 'lucide-react';
import { ordersApi } from '../lib/ordersApi';
import { ROLES } from '../lib/routeConfig';
import { ORDER_TRACKING, SOURCE } from '../lib/statusConfig';
import { formatClientName } from '../lib/formatters';
import { StatusBadge, SourceBadge } from '../components/orders/Badges';
import OrdersFilters from '../components/orders/OrdersFilters';
import RowStatusActions from '../components/orders/RowStatusActions';
import InlineShippingSelect from '../components/orders/InlineShippingSelect';
import OrderDetailSheet from '../components/orders/OrderDetailSheet';
import NewWhatsappOrderDialog from '../components/orders/NewWhatsappOrderDialog';
import ProductThumb from '../components/orders/ProductThumb';
import TruncatedText from '../components/orders/TruncatedText';
import { TooltipProvider } from '../components/ui/tooltip';
import EmptyState from '../components/common/EmptyState';
import { TableRowSkeleton } from '../components/common/TableSkeleton';
import { toastError, toastStatusChange } from '../lib/toast';

const PAGE_SIZE = 25;

// Parse `?statut=&date_from=&date_to=` (and legacy `?status=`) into the
// internal filter shape used by OrdersFilters + ordersApi.list. Called ONCE
// on mount so the deep-link from the dashboard donut pre-fills the filters
// without re-triggering on every URL change afterwards.
function readFiltersFromQuery(params) {
    const out = {};
    const statut = params.get('statut') || params.get('status');
    if (statut) out.status = statut; // may be comma-separated
    const df = params.get('date_from');
    const dt = params.get('date_to');
    if (df) out.date_from = df;
    if (dt) out.date_to = dt;
    // date_preset kept for legacy links.
    const preset = params.get('date_preset');
    if (preset) out.date_preset = preset;
    return out;
}

function formatDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch { return iso; }
}

function formatNumber(n) {
    try { return Number(n).toLocaleString('fr-FR'); } catch { return String(n); }
}

// A filter row is "active" if the user set anything beyond the defaults.
// Used to switch the empty state between "no orders at all" and "filters
// hide everything".
function hasActiveFilters(f) {
    if (!f) return false;
    if (f.status) return true;
    if (f.date_from || f.date_to) return true;
    if (f.date_preset && f.date_preset !== 'all') return true;
    if (f.city || f.product) return true;
    if (f.source || f.shipping_company) return true;
    if (f.call_center_id) return true;
    if (f.search) return true;
    return false;
}

export default function CommandesPage() {
    const { user } = useOutletContext();
    const isAgent = user?.role === ROLES.CC_AGENT;
    const [searchParams, setSearchParams] = useSearchParams();

    // Seed filters from the URL query string ONCE on mount. Later interactions
    // with OrdersFilters keep the state in React only — we don't sync back to
    // the URL to avoid breaking browser history navigation.
    const [filters, setFiltersState] = React.useState(() => readFiltersFromQuery(searchParams));

    // Wrapper: whenever filters are set through the filter row, drop any
    // deep-link query params that no longer match the new filter shape.
    const setFilters = React.useCallback((next) => {
        setFiltersState(next);
        const params = new URLSearchParams(searchParams);
        const dropIf = (urlKey, filterKey) => {
            if (!next || !next[filterKey]) params.delete(urlKey);
            else params.set(urlKey, next[filterKey]);
        };
        dropIf('statut', 'status');
        params.delete('status'); // legacy alias
        dropIf('date_from', 'date_from');
        dropIf('date_to', 'date_to');
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    const [page, setPage] = React.useState(1);
    const [data, setData] = React.useState({ items: [], total: 0, total_pages: 0 });
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [filterValues, setFilterValues] = React.useState(null);

    const [detailOrderId, setDetailOrderId] = React.useState(null);
    const [newOpen, setNewOpen] = React.useState(false);

    // Deep link support (Phase 12.2 — Rappels "Ouvrir" from Mon Dashboard):
    // /commandes?open=<order_id> → open the OrderDetailSheet on mount.
    // The URL param is consumed exactly once then stripped so a subsequent
    // manual close of the sheet doesn't reopen it.
    React.useEffect(() => {
        const openId = searchParams.get('open');
        if (openId) {
            setDetailOrderId(openId);
            const params = new URLSearchParams(searchParams);
            params.delete('open');
            setSearchParams(params, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset to page 1 when filters change
    React.useEffect(() => { setPage(1); }, [filters]);

    const load = React.useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await ordersApi.list({
                ...filters,
                page,
                page_size: PAGE_SIZE,
            });
            setData(res);
        } catch (e) {
            setError(e.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, [filters, page]);

    React.useEffect(() => { load(); }, [load]);
    React.useEffect(() => { ordersApi.filters().then(setFilterValues).catch(() => setFilterValues(null)); }, []);

    // Inline status change → discrete toast (2 s, bottom-right).
    const onRowStatusChange = async (orderId, newStatus) => {
        try {
            const res = await ordersApi.patch(orderId, { order_tracking: newStatus });
            setData((prev) => ({
                ...prev,
                items: prev.items.map((o) => o.id === orderId ? res.order : o),
            }));
            const label = ORDER_TRACKING[newStatus]?.label || newStatus;
            toastStatusChange(`Statut → ${label}`);
        } catch (e) {
            toastError(typeof e.detail === 'string' ? e.detail : (e.message || 'Erreur lors du changement de statut'));
        }
    };

    // Inline shipping company change → same toast pattern (Phase 13.5).
    const onRowShippingChange = async (orderId, newShipping) => {
        try {
            const res = await ordersApi.patch(orderId, { shipping_company: newShipping });
            setData((prev) => ({
                ...prev,
                items: prev.items.map((o) => o.id === orderId ? res.order : o),
            }));
            toastStatusChange(newShipping ? `Transporteur → ${newShipping.toUpperCase()}` : 'Transporteur retiré');
        } catch (e) {
            toastError(typeof e.detail === 'string' ? e.detail : (e.message || 'Erreur lors du changement de transporteur'));
        }
    };

    const applyOrderPatchToList = React.useCallback((updated) => {
        setData((prev) => ({
            ...prev,
            items: prev.items.map((o) => o.id === updated.id ? updated : o),
        }));
    }, []);

    const onCreated = (newOrder) => {
        // Refresh list — new order should be at the top given date desc sort.
        load();
    };

    const resetFilters = React.useCallback(() => {
        setFilters({});
    }, [setFilters]);

    const callCenters = filterValues?.call_centers || [];
    const shippingCompanies = filterValues?.shipping_companies || [];
    const productCatalog = filterValues?.product_catalog || [];
    const filtersActive = hasActiveFilters(filters);
    // Phase 11.7 (Feb 2026): 3 columns removed at client's request
    // (CONFIRMÉ PAR, NB COMMANDES, COMMENTAIRE). Phase 11.8 added ADRESSE
    // (after VILLE). Phase 11.9 removed RÉGION from the table (kept in DB
    // + API, but hidden per client decision — only Adresse remains in the
    // shipping-location group).
    //
    // TH count (used by empty/error/skeleton rows): 14 informational + 1
    // action = 15 for super_admin/manager; 13 + 1 = 14 for cc_agent
    // (CALL CENTER hidden).
    const colspan = isAgent ? 14 : 15;

    return (
        <div data-testid="commandes-page">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-[#1A1D2E]">Commandes</h2>
                    <span
                        data-testid="orders-total-badge"
                        className="inline-flex items-center h-6 px-2.5 rounded-full text-xs font-semibold bg-[#4B3FD8]/10 text-[#4B3FD8]"
                    >
                        {formatNumber(data.total)}
                    </span>
                </div>
                <button
                    type="button"
                    data-testid="new-order-button"
                    onClick={() => setNewOpen(true)}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-medium hover:shadow-lg hover:shadow-[#4B3FD8]/25 transition-shadow"
                >
                    <Plus size={16} strokeWidth={2} />
                    Nouvelle commande
                </button>
            </div>

            <OrdersFilters filters={filters} onChange={setFilters} filterValues={filterValues} user={user} />

            {/* Table */}
            <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <TooltipProvider delayDuration={300}>
                    <table data-testid="orders-table" className="w-full text-sm table-fixed">
                        <colgroup>
                            <col style={{ width: '220px' }} />
                            <col style={{ width: '100px' }} />
                            <col style={{ width: '130px' }} />
                            <col style={{ width: '130px' }} />
                            <col style={{ width: '110px' }} />
                            <col style={{ width: '200px' }} />
                            <col style={{ width: '50px'  }} />
                            <col style={{ width: '160px' }} />
                            <col style={{ width: '80px'  }} />
                            <col style={{ width: '130px' }} />
                            <col style={{ width: '130px' }} />
                            <col style={{ width: '100px' }} />
                            {!isAgent && <col style={{ width: '80px' }} />}
                            <col style={{ width: '130px' }} />
                            <col style={{ width: '40px'  }} />
                        </colgroup>
                        <thead>
                            <tr className="bg-[#F5F6FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                                <Th>Produit</Th>
                                <Th>Date</Th>
                                <Th>Nom complet</Th>
                                <Th>Téléphone</Th>
                                <Th>Ville</Th>
                                <Th>Adresse</Th>
                                <Th className="text-right">Qté</Th>
                                <Th>Variante</Th>
                                <Th className="text-right">Prix</Th>
                                <Th>Statut</Th>
                                <Th>Transporteur</Th>
                                <Th>Source</Th>
                                {!isAgent && <Th>Call Center</Th>}
                                <Th>Order ID</Th>
                                <Th />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <TableRowSkeleton
                                    rows={PAGE_SIZE}
                                    columns={colspan}
                                    testId="orders-skeleton"
                                />
                            )}
                            {!loading && error && (
                                <tr>
                                    <td data-testid="orders-error" colSpan={colspan} className="text-center py-12 text-[#EF4444] text-sm">{typeof error === 'string' ? error : 'Erreur'}</td>
                                </tr>
                            )}
                            {!loading && !error && data.items.length === 0 && (
                                <tr>
                                    <td colSpan={colspan} className="py-2">
                                        {filtersActive ? (
                                            <EmptyState
                                                testId="orders-empty-filtered"
                                                Icon={Filter}
                                                title="Aucune commande ne correspond à vos filtres"
                                                description="Essayez de modifier ou d'effacer les filtres appliqués."
                                                action={{ label: 'Réinitialiser les filtres', onClick: resetFilters, testId: 'orders-reset-filters' }}
                                            />
                                        ) : (
                                            <EmptyState
                                                testId="orders-empty"
                                                Icon={Package}
                                                title="Aucune commande pour le moment"
                                                description="Les commandes créées manuellement ou reçues via Shopify apparaîtront ici."
                                                action={{ label: 'Nouvelle commande', Icon: Plus, onClick: () => setNewOpen(true), testId: 'orders-empty-new' }}
                                            />
                                        )}
                                    </td>
                                </tr>
                            )}
                            {!loading && !error && data.items.map((o, idx) => {
                                // Phase 16 — items[]: multi-item orders use
                                // `line_items` (2+), everything else uses a
                                // synthesised 1-item list from the flat
                                // product_name/product_variant/quantity so
                                // the render code below stays uniform.
                                const items = Array.isArray(o.line_items) && o.line_items.length > 0
                                    ? o.line_items
                                    : [{
                                        product_name: o.product_name,
                                        product_variant: o.product_variant,
                                        quantity: o.quantity,
                                        product_image_url: o.product_image_url,
                                    }];
                                const isMulti = items.length > 1;
                                return (
                                <tr
                                    key={o.id}
                                    data-testid="orders-row"
                                    data-order-id={o.id}
                                    data-multi-item={isMulti ? 'true' : 'false'}
                                    className={`group ${idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F6FA]'}`}
                                >
                                    {/* 1 — Produit (vignette + nom empilé par item, avec Phase 16.3 :
                                        regroupement des line items consécutifs ayant le même
                                        product_name — le nom + l'image ne s'affichent que pour
                                        le premier item du groupe, avec un badge "× N" si N > 1.
                                        Les items suivants du même groupe restent dans le DOM
                                        avec `invisible` pour conserver strictement le rythme
                                        vertical de VARIANTE / QTÉ ligne par ligne. */}
                                    <Td>
                                        <div data-testid="cell-product-stack" className="space-y-1.5">
                                            {items.map((it, i) => {
                                                const isGroupStart = i === 0 || items[i - 1].product_name !== it.product_name;
                                                let groupSize = 1;
                                                if (isGroupStart) {
                                                    while (i + groupSize < items.length && items[i + groupSize].product_name === it.product_name) groupSize++;
                                                }
                                                return (
                                                    <div
                                                        key={i}
                                                        data-testid={`cell-product-item-${i}`}
                                                        aria-hidden={isGroupStart ? undefined : true}
                                                        className={`${i > 0 ? (isGroupStart ? 'pt-1.5 border-t border-[#F3F4F6]' : 'pt-1.5') : ''} ${isGroupStart ? '' : 'invisible'}`}
                                                    >
                                                        <div className="flex items-start gap-3 min-w-0">
                                                            <ProductThumb src={it.product_image_url} alt={it.product_name} size="sm" />
                                                            <div className="pt-0.5 min-w-0">
                                                                <TruncatedText
                                                                    text={it.product_name}
                                                                    lines={2}
                                                                    testId="cell-product-name"
                                                                />
                                                                {isGroupStart && groupSize > 1 && (
                                                                    <div
                                                                        data-testid="cell-product-count-badge"
                                                                        className="text-xs text-[#6B7280] mt-0.5"
                                                                    >
                                                                        × {groupSize}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Td>
                                    {/* 2 — Date */}
                                    <Td className="whitespace-nowrap">{formatDate(o.order_date)}</Td>
                                    {/* 3 — Nom complet (2 lignes max, tooltip si tronqué) */}
                                    <Td>
                                        <TruncatedText
                                            text={formatClientName(o.full_name)}
                                            lines={2}
                                            testId="cell-full-name"
                                        />
                                    </Td>
                                    {/* 4 — Téléphone (DONNÉE CRITIQUE : toujours entièrement visible, jamais tronqué) */}
                                    <Td>
                                        <span
                                            data-testid="cell-phone"
                                            className="tabular-nums whitespace-nowrap"
                                        >
                                            {o.phone || '—'}
                                        </span>
                                    </Td>
                                    {/* 5 — Ville (2 lignes max, tooltip si tronqué très rare) */}
                                    <Td>
                                        <TruncatedText
                                            text={o.city}
                                            lines={2}
                                            testId="cell-city"
                                        />
                                    </Td>
                                    {/* 6 — Adresse (2 lignes max, tooltip si tronqué) */}
                                    <Td>
                                        <TruncatedText
                                            text={o.address}
                                            lines={2}
                                            testId="cell-address"
                                        />
                                    </Td>
                                    {/* 7 — Qté (centré, une ligne par item) */}
                                    <Td className="text-center tabular-nums">
                                        <div data-testid="cell-quantity-stack" className="space-y-1.5">
                                            {items.map((it, i) => (
                                                <div
                                                    key={i}
                                                    className={`${i > 0 ? 'pt-1.5 border-t border-[#F3F4F6]' : ''}`}
                                                >
                                                    {it.quantity ?? '—'}
                                                </div>
                                            ))}
                                        </div>
                                    </Td>
                                    {/* 8 — Variante (une ligne par item) */}
                                    <Td>
                                        <div data-testid="cell-variant-stack" className="space-y-1.5">
                                            {items.map((it, i) => (
                                                <div
                                                    key={i}
                                                    className={`${i > 0 ? 'pt-1.5 border-t border-[#F3F4F6]' : ''}`}
                                                >
                                                    <TruncatedText
                                                        text={it.product_variant}
                                                        lines={2}
                                                        testId="cell-variant"
                                                        className="text-[#6B7280] text-xs"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </Td>
                                    {/* 9 — Prix (au niveau commande, non ventilé) */}
                                    <Td className="text-right tabular-nums whitespace-nowrap">{Number(o.price || 0).toFixed(2)}</Td>
                                    {/* 10 — Statut */}
                                    <Td>
                                        <RowStatusActions
                                            value={o.order_tracking}
                                            onChange={(s) => onRowStatusChange(o.id, s)}
                                        />
                                    </Td>
                                    {/* 11 — Transporteur */}
                                    <Td>
                                        <InlineShippingSelect
                                            value={o.shipping_company}
                                            options={shippingCompanies}
                                            onChange={(n) => onRowShippingChange(o.id, n)}
                                            testId={`row-shipping-${o.id}`}
                                        />
                                    </Td>
                                    {/* 12 — Source */}
                                    <Td><SourceBadge source={o.source} /></Td>
                                    {/* 13 — Call Center (masquée pour cc_agent) */}
                                    {!isAgent && <Td>{o.call_center_name || '—'}</Td>}
                                    {/* 14 — Order ID (1 ligne, tooltip si tronqué) */}
                                    <Td className="font-mono text-xs text-[#4B3FD8]">
                                        <TruncatedText
                                            text={`#${o.short_id}`}
                                            lines={1}
                                            testId="cell-order-id"
                                            className="font-mono"
                                        />
                                    </Td>
                                    {/* Actions */}
                                    <Td className="text-right">
                                        <button
                                            type="button"
                                            data-testid="row-open-detail"
                                            onClick={() => setDetailOrderId(o.id)}
                                            className="p-1 rounded hover:bg-[#4B3FD8]/10 text-[#6B7280]"
                                            aria-label="Ouvrir le détail"
                                        >
                                            <ChevronRight size={18} strokeWidth={1.75} />
                                        </button>
                                    </Td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </TooltipProvider>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E7EB] text-sm">
                    <div className="text-[#6B7280]">
                        {data.total > 0 ? (
                            <>Page {page} sur {data.total_pages || 1} · {formatNumber(data.total)} commandes</>
                        ) : (
                            <>—</>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            data-testid="pagination-prev"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className="w-9 h-9 rounded-md text-[#6B7280] hover:bg-[#F5F6FA] disabled:opacity-40 inline-flex items-center justify-center"
                        >
                            <ChevronLeft size={16} strokeWidth={1.75} />
                        </button>
                        <span data-testid="pagination-current" className="px-3 text-[#1A1D2E] text-sm font-medium">
                            {page}
                        </span>
                        <button
                            type="button"
                            data-testid="pagination-next"
                            disabled={data.total_pages && page >= data.total_pages}
                            onClick={() => setPage((p) => p + 1)}
                            className="w-9 h-9 rounded-md text-[#6B7280] hover:bg-[#F5F6FA] disabled:opacity-40 inline-flex items-center justify-center"
                        >
                            <ChevronRight size={16} strokeWidth={1.75} />
                        </button>
                    </div>
                </div>
            </div>

            <OrderDetailSheet
                open={!!detailOrderId}
                onOpenChange={(v) => !v && setDetailOrderId(null)}
                orderId={detailOrderId}
                user={user}
                callCenters={callCenters}
                shippingCompanies={shippingCompanies}
                productCatalog={productCatalog}
                onOrderChanged={applyOrderPatchToList}
            />

            <NewWhatsappOrderDialog
                open={newOpen}
                onOpenChange={setNewOpen}
                user={user}
                callCenters={callCenters}
                productCatalog={productCatalog}
                onCreated={onCreated}
            />
        </div>
    );
}

function Th({ children, className = '', ...rest }) {
    return <th className={`text-left px-3 py-2 font-semibold ${className}`} {...rest}>{children}</th>;
}
function Td({ children, className = '', ...rest }) {
    return <td className={`px-3 py-2 align-top text-[#1A1D2E] ${className}`} {...rest}>{children}</td>;
}
